import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material'
import hipersendService from '../../services/hipersend'
import LinkWithQuery from '../LinkWithQuery'
import { tokensDoDisparo } from '../shared/disparo/tokens'
import { linkDeRecargaSeguro } from '../../libs/suspensaoOficial'
import {
  numerosDoDisparo, descreverMensagem, prazoRestante, quandoFoi, reais, numero
} from '../../libs/disparos'
import '../shared/disparo/disparo.css'

// Acompanhamento de UM disparo: o que já saiu, o que chegou, o que falhou e o
// que ainda espera. Consulta o master a cada 5 s enquanto o disparo pode mudar
// (na fila, enviando ou pausado) e para quando ele termina. O envio acontece
// no servidor: fechar a tela não interrompe nada.

const INTERVALO_MS = 5000
const MOTIVO_EXPIRADO = 'Saldo insuficiente — disparo expirado'

function Pilula({ status, canceledReason, adiadas }) {
  if (status === 'pending') return <span className="dp-pill vivo"><i />Começa em instantes</span>
  if (status === 'done' && adiadas > 0) return <span className="dp-pill pausa"><i />Aguardando o limite diário</span>
  if (status === 'processing') return <span className="dp-pill vivo"><i />Enviando</span>
  if (status === 'paused_saldo') return <span className="dp-pill pausa"><i />Pausado</span>
  if (status === 'done') return <span className="dp-pill fim"><i />Concluído</span>
  if (status === 'canceled') {
    return <span className="dp-pill fim"><i />{canceledReason === MOTIVO_EXPIRADO ? 'Expirou sem saldo' : 'Cancelado'}</span>
  }
  return <span className="dp-pill fim"><i />Não concluído</span>
}

function Kpi({ cor, valor, rotulo }) {
  return (
    <div className="dp-kpi">
      <span className="r" style={{ background: cor }} />
      <div><b>{numero(valor)}</b><span>{rotulo}</span></div>
    </div>
  )
}

export default function PainelDoDisparo({ api, secret, disparoId, linkRecarga, onMudou }) {
  const theme = useTheme()
  const [detalhe, setDetalhe] = useState(null)
  const [erro, setErro] = useState('')
  const [confirmando, setConfirmando] = useState(false)
  const [cancelando, setCancelando] = useState(false)
  const avisar = useRef(onMudou)
  avisar.current = onMudou

  const buscar = useCallback(async () => {
    try {
      const d = await hipersendService.disparos.detalhe(api, secret, disparoId)
      setDetalhe(d)
      setErro('')
      if (avisar.current) avisar.current(d)
      return d
    } catch (e) {
      setErro(e?.response?.data?.message || 'Não foi possível atualizar o andamento agora')
      return null
    }
  }, [api, secret, disparoId])

  // Primeira leitura e, enquanto o disparo pode mudar, uma a cada 5 s. A
  // próxima só é marcada depois que a anterior volta — sem consultas
  // empilhadas numa rede lenta.
  useEffect(() => {
    let vivo = true
    let espera = null
    setDetalhe(null)
    setConfirmando(false)

    const rodar = async () => {
      const d = await buscar()
      if (!vivo) return
      // Segue olhando enquanto algo ainda pode sair — inclusive o lote já
      // concluído com mensagens adiadas pelo limite diário do número.
      if (!d || numerosDoDisparo(d).podeCancelar) {
        espera = setTimeout(rodar, INTERVALO_MS)
      }
    }
    rodar()

    return () => {
      vivo = false
      clearTimeout(espera)
    }
  }, [buscar])

  const cancelar = async () => {
    setCancelando(true)
    try {
      await hipersendService.disparos.cancelar(api, secret, disparoId)
      setConfirmando(false)
      await buscar()
    } catch (e) {
      setErro(e?.response?.data?.message || 'Não foi possível cancelar agora. Tente de novo.')
    } finally {
      setCancelando(false)
    }
  }

  if (!detalhe) {
    return (
      <section className="dp dp-acomp" style={tokensDoDisparo(theme)} aria-busy="true">
        <span className="dp-extra">{erro || 'Carregando o andamento do disparo…'}</span>
      </section>
    )
  }

  const n = numerosDoDisparo(detalhe)
  const pausado = detalhe.status === 'paused_saldo'
  const link = linkDeRecargaSeguro(linkRecarga)
  const prazo = prazoRestante(detalhe.expiraEm)
  const faltam = Math.max(0, n.total - n.sairam)
  const largura = (v) => `${n.total ? Math.min(100, (v / n.total) * 100) : 0}%`
  const resumo = [
    descreverMensagem(detalhe.mensagem),
    `${numero(n.total)} ${n.total === 1 ? 'destinatário' : 'destinatários'}`,
    detalhe.estimativa?.total > 0 ? `até ${reais(detalhe.estimativa.total)}` : null
  ].filter(Boolean).join(' · ')

  const quarto = pausado
    ? { cor: 'var(--dp-warn)', valor: n.retidasSaldo, rotulo: n.retidasSaldo === 1 ? 'retida por saldo' : 'retidas por saldo' }
    : n.ativo
      ? { cor: 'var(--dp-faint)', valor: n.naFila, rotulo: 'na fila' }
      : { cor: 'var(--dp-faint)', valor: n.canceladas, rotulo: n.canceladas === 1 ? 'cancelada' : 'canceladas' }

  return (
    <section className="dp dp-acomp" style={tokensDoDisparo(theme)}>
      <div className="dp-acomp-cab">
        <div>
          <Pilula status={detalhe.status} canceledReason={detalhe.canceledReason} adiadas={n.adiadas} />
          <h3>Disparo de {quandoFoi(detalhe.criadoEm)}</h3>
          <p>{resumo}</p>
        </div>
        {n.podeCancelar && !confirmando && (
          <button type="button" className="dp-btn perigo" onClick={() => setConfirmando(true)}>
            Cancelar o que ainda não saiu
          </button>
        )}
      </div>

      {confirmando && (
        <div className="dp-confirma-cancelar" role="alert">
          <span>
            Cancelar {numero(faltam)} {faltam === 1 ? 'envio que ainda não saiu' : 'envios que ainda não saíram'}?
            O que já saiu continua valendo. Não dá para desfazer.
          </span>
          <div className="dp-linha-botoes">
            <button type="button" className="dp-btn" onClick={() => setConfirmando(false)} disabled={cancelando}>
              Manter disparo
            </button>
            <button type="button" className="dp-btn perigo-cheio" onClick={cancelar} disabled={cancelando}>
              {cancelando ? 'Cancelando…' : 'Sim, cancelar'}
            </button>
          </div>
        </div>
      )}

      {pausado && (
        <div className="dp-aviso-pausa">
          <div>
            <b>Pausado: o saldo acabou no meio do disparo</b>
            <span>
              Volta sozinho assim que a recarga cair.
              {prazo ? ` Sem recarga, os ${numero(faltam)} restantes são cancelados em ${prazo}.` : ''}
            </span>
          </div>
          {link && (
            <a className="dp-btn pri" href={link} target="_blank" rel="noopener noreferrer">Recarregar saldo</a>
          )}
        </div>
      )}

      <div className="dp-andou">
        <strong>{numero(n.sairam)}</strong>
        <span>
          de {numero(n.total)} já saíram
          {detalhe.status === 'processing' ? ' · os próximos seguem o ritmo do plano' : ''}
        </span>
      </div>

      <div className="dp-progresso" aria-hidden="true">
        <i style={{ width: largura(n.entregues), background: 'var(--dp-accent)' }} />
        <i style={{ width: largura(n.aCaminho), background: 'var(--dp-info)' }} />
        <i style={{ width: largura(n.falhas), background: 'var(--dp-danger)' }} />
        <i style={{ width: largura(n.retidas), background: 'var(--dp-warn)' }} />
      </div>

      <div className="dp-kpis">
        <Kpi cor="var(--dp-accent)" valor={n.entregues} rotulo="entregues" />
        <Kpi cor="var(--dp-info)" valor={n.aCaminho} rotulo="aceitas, sem confirmação" />
        <Kpi cor="var(--dp-danger)" valor={n.falhas} rotulo="falharam" />
        <Kpi {...quarto} />
      </div>

      {n.adiadas > 0 && (
        <span className="dp-extra">
          {numero(n.adiadas)} {n.adiadas === 1 ? 'adiada' : 'adiadas'} pelo limite diário de segurança do número — saem
          sozinhas no próximo período.
        </span>
      )}
      {n.retidasSmartFilter > 0 && (
        <span className="dp-extra">
          {numero(n.retidasSmartFilter)} {n.retidasSmartFilter === 1 ? 'segura' : 'seguras'} pelo Filtro de números.
        </span>
      )}
      {erro && <span className="dp-erro">{erro}</span>}

      <div className="dp-acomp-pe">
        <span className="dp-extra">Falhas ficam em Relatórios com o motivo de cada uma.</span>
        <LinkWithQuery to="/reports" className="dp-btn fantasma">Abrir em Relatórios</LinkWithQuery>
      </div>
    </section>
  )
}
